import React from 'react';
import * as R from 'ramda';
import {
  ArrayField,
  Datagrid,
  Error,
  Loading,
  SelectField,
  TextField,
  useQuery,
} from 'react-admin';
import { Box, Button, makeStyles } from '@material-ui/core';
import { OpenInNew } from '@material-ui/icons';

import {
  AssessmentScoreChip,
  DateField,
  DeliveryTitle,
  NewProgressField,
  OfflineList,
} from 'resources/common';
import { assignmentStatus } from 'data';

import { FullNameField } from 'resources/students/fields';
const useStyles = makeStyles({
  hiddenHeader: { display: 'none' },
  hiddenHeaderRow: { display: 'none' },
});

const ReviewInQualifiedButton = ({ record: student }) => {
  const canReview = R.allPass([
    R.hasPath(['external_ids', 'qualified']),
    R.hasPath(['challenge', 'assessment_result_id']),
    R.hasPath(['challenge', 'solution_id']),
    R.complement(R.pathEq(['challenge', 'status'], 'pending')),
  ])(student);
  if (canReview) {
    const [candidateId, resultId, solutionId] = R.paths([
      ['external_ids', 'qualified'],
      ['challenge', 'assessment_result_id'],
      ['challenge', 'solution_id'],
    ])(student);
    const url = `https://www.qualified.io/hire/candidates/all/${candidateId}/${resultId}/review/${solutionId}`;
    return (
      <Button
        size="small"
        color="primary"
        target="_blank"
        href={url}
        label="Review"
      >
        <OpenInNew style={{ paddingRight: '0.2em' }} />
        Review
      </Button>
    );
  } else {
    return null;
  }
};

const ProgressField = ({ record = {} }) => {
  const completion =
    record && typeof record.completion === 'number' ? record.completion : 0;
  return <NewProgressField value={completion} text={`${completion}%`} />;
};

const StudentScoreField = ({ record: student }) => {
  const isPending = R.pathEq(['challenge', 'status'], 'pending', student);
  if (isPending) return null;
  return (
    <AssessmentScoreChip
      record={student}
      scoreSource="challenge.score"
      cutScoreSource="challenge.assessment_cut_score"
    />
  );
};

const StudentList = ({ record: challenge, course, students }) => {
  const tableData = R.pipe(
    R.map((student) => {
      const studentChallenge = R.pipe(
        R.path([course, 'challenges']),
        R.find(R.propEq('challenge_id', challenge.challenge_id))
      )(student);
      return { ...student, challenge: studentChallenge };
    }),
    R.sortBy(R.prop('status'))
  )(students);

  return (
    <OfflineList list={tableData} uniqueKey="id">
      <Datagrid>
        <FullNameField />
        <SelectField
          label="Status"
          choices={assignmentStatus}
          source="challenge.status"
        />
        <DateField label="Submit Date" source="challenge.date" />
        <StudentScoreField label="Grade" textAlign="center" />
        <ReviewInQualifiedButton label="Review In Qualified" sortable={false} />
      </Datagrid>
    </OfflineList>
  );
};

const ChallengeList = ({ record: assessment, cohort, course, students }) => {
  const classes = useStyles();
  const challengesMap = R.pipe(
    R.pathOr([], [course, 'challenges']),
    R.filter(R.propEq('assessment_id', assessment.assessment_id)),
    R.groupBy(R.prop('challenge_id')),
    R.map(R.path(['0'])),
    R.map(
      R.applySpec({
        title: R.prop('title'),
        challenge_id: R.prop('challenge_id'),
      })
    )
  )(cohort);

  const tableData = R.pipe(
    R.chain(R.pathOr([], [course, 'challenges'])),
    R.filter(R.propEq('assessment_id', assessment.assessment_id)),
    R.groupBy(R.prop('challenge_id')),
    R.map(
      R.pipe(
        R.reject(R.propEq('status', 'pending')),
        R.pluck('score'),
        (scores) => ({
          completion: Math.round((100 * scores.length) / students.length),
          average_score: R.defaultTo('noAverage', Math.round(R.mean(scores))),
        })
      )
    ),
    R.mergeDeepLeft(challengesMap),
    R.values
  )(students);

  return (
    <OfflineList list={R.reverse(tableData)} uniqueKey="challenge_id">
      <Datagrid
        rowClick="expand"
        expand={<StudentList course={course} students={students} />}
        classes={{ headerRow: classes.hiddenHeaderRow }}
      >
        <TextField
          label=""
          headerClassName={classes.hiddenHeader}
          source="title"
        />
        <ProgressField label="% Completion" source="completion" />;
        <AssessmentScoreChip
          label="Cohort Average"
          textAlign="center"
          scoreSource="average_score"
          cutScoreSource="assessment_cut_score"
        />
      </Datagrid>
    </OfflineList>
  );
};

const AssessmentList = ({ cohort, course, students }) => {
  const assessmentsMap = R.pipe(
    R.pathOr([], [course, 'challenges']),
    R.groupBy(R.prop('assessment_id')),
    R.map(
      R.applySpec({
        challenge_count: R.length,
        title: R.path(['0', 'assessment_title']),
        assessment_id: R.path(['0', 'assessment_id']),
        assessment_cut_score: R.path(['0', 'assessment_cut_score']),
        required: R.path(['0', 'required']),
      })
    )
  )(cohort);

  const { data: apiAssessments } = useQuery({
    type: 'getManyReference',
    resource: 'assessments',
    payload: {
      target: 'cohort',
      id: cohort.id,
      pagination: { perPage: 100 },
      filter: R.reject(R.isNil, { course }),
      sort: { field: 'first_name', order: 'ASC' },
    },
  });

  const flatRows = R.pipe(
    R.chain(R.pathOr([], [course, 'challenges'])),
    R.groupBy(R.prop('assessment_id')),
    R.map(R.pipe(R.reject(R.propEq('status', 'pending')), R.pluck('score'))),
    R.mapObjIndexed((scores, assessment_id) => ({
      average_score: R.defaultTo(undefined, Math.round(R.mean(scores))),
      completion: R.defaultTo(
        0,
        Math.round(
          (100 * scores.length) /
            (assessmentsMap[assessment_id].challenge_count * students.length)
        )
      ),
    })),
    R.mergeDeepLeft(assessmentsMap),
    R.values
  )(students);

  const moduleOf = (item) =>
    R.pathOr('Unknown module', ['path', 1, 'display_name'], item);
  const submoduleOf = (item) =>
    R.pathOr('Unknown submodule', ['path', 2, 'display_name'], item);
  const idToModule = R.pipe(
    R.groupBy(R.prop('assessment_id')),
    R.map(R.head),
    R.map((it) => ({
      module: moduleOf(it),
      submodule: submoduleOf(it),
      path: it.path,
    }))
  )(apiAssessments || []);

  const rowsWithGrouping = flatRows.map((row) => ({
    ...row,
    ...(idToModule[row.assessment_id] || {
      module: 'Unknown module',
      submodule: 'Unknown submodule',
      path: undefined,
    }),
  }));

  const providerStructure = R.pipe(
    R.groupBy(moduleOf),
    R.toPairs,
    R.map(([moduleName, items]) => ({
      module: moduleName,
      submodules: R.pipe(
        R.groupBy(submoduleOf),
        R.toPairs,
        R.map(([submoduleName, items2]) => ({
          submodule: submoduleName,
          units: R.pipe(
            R.map(R.pathOr('', ['path', 3, 'display_name'])),
            R.reject(R.isEmpty),
            R.uniq,
            R.reverse // invert units order
          )(items2),
        })),
        R.reverse // invert submodules order
      )(items),
    })),
    R.reverse // invert modules order
  )(apiAssessments || []);

  const moduleOrder = providerStructure.map((m) => m.module);
  const submoduleOrderMap = providerStructure.reduce((acc, m) => {
    acc[m.module] = m.submodules.map((sm) => sm.submodule);
    return acc;
  }, {});
  const unitOrderMap = providerStructure.reduce((acc, m) => {
    m.submodules.forEach((sm) => {
      acc[`${m.module}::${sm.submodule}`] = sm.units;
    });
    return acc;
  }, {});

  const indexOfOrMax = (arr, val) => {
    const idx = arr.indexOf(val);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  };

  const modules = R.pipe(
    R.groupBy(R.prop('module')),
    R.toPairs,
    R.sortBy(([moduleName]) => indexOfOrMax(moduleOrder, moduleName)),
    R.map(([moduleName, moduleRows]) => ({
      id: moduleName,
      display_name: moduleName,
      rows: R.pipe(
        R.groupBy(R.prop('submodule')),
        R.toPairs,
        R.sortBy(([submoduleName]) =>
          indexOfOrMax(submoduleOrderMap[moduleName] || [], submoduleName)
        ),
        R.chain(([submoduleName, subRows]) =>
          R.sortBy((row) =>
            indexOfOrMax(
              unitOrderMap[`${moduleName}::${submoduleName}`] || [],
              R.pathOr('', ['path', 3, 'display_name'], row)
            )
          )(subRows)
        )
      )(moduleRows),
    }))
  )(rowsWithGrouping);

  const ModuleAssessmentsTable = ({ record }) => {
    const tableData = R.propOr([], 'rows', record);
    if (R.isEmpty(tableData)) return null;
    return (
      <OfflineList list={tableData} uniqueKey="assessment_id">
        <Datagrid
          rowClick="expand"
          expand={
            <ChallengeList
              cohort={cohort}
              course={course}
              students={students}
            />
          }
        >
          <DeliveryTitle />
          <TextField label="# of Challenges" source="challenge_count" />
          <ProgressField label="% Completion" source="completion" />
          <AssessmentScoreChip
            label="Cohort Average"
            textAlign="center"
            scoreSource="average_score"
            cutScoreSource="assessment_cut_score"
          />
        </Datagrid>
      </OfflineList>
    );
  };

  const ModuleDetail = ({ record }) => (
    <ModuleAssessmentsTable record={record} />
  );

  return (
    <ArrayField source="modules" record={{ modules }}>
      <Datagrid rowClick="expand" expand={<ModuleDetail />}>
        <TextField label="Module" source="display_name" sortable={false} />
      </Datagrid>
    </ArrayField>
  );
};

function ByAssessmentsSubtab(props) {
  const { record: cohort, course } = props;

  const {
    data: students,
    loading: studentsLoading,
    error: studentsError,
  } = useQuery({
    type: 'getManyReference',
    resource: 'students',
    payload: {
      target: 'cohort',
      id: cohort.id,
      pagination: {
        perPage: 100,
      },
      filter: { course: true },
      sort: { field: 'first_name', order: 'ASC' },
    },
  });

  if (studentsLoading)
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );
  if (studentsError)
    return (
      <Box p="16px">
        <Error error={studentsError} errorInfo={studentsError} />
      </Box>
    );
  if (!students) return null;

  return <AssessmentList cohort={cohort} course={course} students={students} />;
}

export default ByAssessmentsSubtab;
